import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Avatar,
  Button,
  Checkbox,
  Dialog,
  DialogContentContainer,
  Divider,
  Dropdown,
  Flex,
  Heading,
  Icon,
  IconButton,
  Search,
  Text,
} from '@vibe/core';
import { Person, DropdownChevronDown, Update } from '@vibe/icons';
import { useTimesheetStore } from '../../stores/timesheetStore';
import { useApprovalStore } from '../../stores/approvalStore';
import { useUsersStore } from '../../stores/usersStore';
import { listTimesheets } from '../../api/timesheet-approval.api';
import TimesheetPendingList from './TimesheetPendingList';
import TimesheetHistoryList from './TimesheetHistoryList';
import TimesheetDetailPanel from './TimesheetDetailPanel';
import type { TimesheetGroupBy } from '../../types/timesheet.types';
import type { TimesheetSubmission } from '../../types/timesheet-approval.types';

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'reopened', label: 'Reopened' },
  { value: 'submitted', label: 'Pending' },
];

export interface TimesheetApproverViewProps {
  currentUserId: string;
  isAdmin: boolean;
}

const APPROVAL_PENDING = 'approval_pending' as TimesheetGroupBy;

/**
 * Main approver container rendered when the sidebar shows
 * "Pending Approvals" or "Approval History".
 *
 * Layout: list on the left, detail panel on the right.
 */
const TimesheetApproverView: React.FC<TimesheetApproverViewProps> = ({
  currentUserId,
  isAdmin,
}) => {
  const groupBy = useTimesheetStore((s) => s.groupBy);
  const users = useUsersStore((s) => s.users);

  const setPendingTimesheets = useApprovalStore((s) => s.setPendingTimesheets);
  const setPendingCount = useApprovalStore((s) => s.setPendingCount);
  const setSelectedTimesheetId = useApprovalStore(
    (s) => s.setSelectedTimesheetId,
  );

  const isPending = groupBy === APPROVAL_PENDING;
  const [refreshing, setRefreshing] = useState(false);

  // ─── Filter state (shared across pending & history views) ──────────
  const [statusFilter, setStatusFilter] = useState(''); // history only
  const [selectedUserIds, setSelectedUserIds] = useState<number[]>([]);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [userSearchValue, setUserSearchValue] = useState('');

  const userOptionsList = useMemo(
    () => users.map((u) => ({ id: u.id, name: u.name, avatarUrl: u.photo_thumb_small ?? undefined })),
    [users],
  );

  const lowerUserSearch = userSearchValue.toLowerCase();
  const filteredUserOptions = useMemo(
    () => userOptionsList.filter((u) => u.name.toLowerCase().includes(lowerUserSearch)),
    [userOptionsList, lowerUserSearch],
  );

  const toggleUser = useCallback(
    (id: number) => {
      setSelectedUserIds((prev) =>
        prev.includes(id) ? prev.filter((u) => u !== id) : [...prev, id],
      );
    },
    [],
  );

  const userFilterParam = useMemo(
    () => (selectedUserIds.length > 0 ? selectedUserIds.join(',') : undefined),
    [selectedUserIds],
  );

  const userTriggerLabel = useMemo(() => {
    if (selectedUserIds.length === 0) return 'All users';
    if (selectedUserIds.length === 1) {
      const user = userOptionsList.find((u) => u.id === selectedUserIds[0]);
      return user?.name ?? '1 user';
    }
    return `${selectedUserIds.length} users`;
  }, [selectedUserIds, userOptionsList]);

  // Build user name map for display
  const userNameMap = useMemo(() => {
    const map: Record<string, string> = {};
    for (const u of users) {
      map[String(u.id)] = u.name;
    }
    return map;
  }, [users]);

  // Fetch pending timesheets and update store + badge count
  const refreshPending = useCallback(async () => {
    if (!currentUserId) return;
    setRefreshing(true);
    try {
      const response = await listTimesheets({
        status: 'submitted',
        approverUserId: currentUserId,
        userId: userFilterParam,
        from: fromDate || undefined,
        to: toDate || undefined,
      });
      setPendingTimesheets(response.data);
      setPendingCount(response.total);
    } catch {
      // Silently ignore refresh errors
    } finally {
      setRefreshing(false);
    }
  }, [currentUserId, userFilterParam, fromDate, toDate, setPendingTimesheets, setPendingCount]);

  // Fetch pending list on mount
  useEffect(() => {
    if (isPending) {
      refreshPending();
    }
  }, [isPending, refreshPending]);

  // Refresh pending list after an action (approve/reject/reopen)
  const handleActionComplete = useCallback(
    async (updated: TimesheetSubmission) => {
      // If it was approved/rejected, remove from pending list
      if (updated.status !== 'submitted') {
        setSelectedTimesheetId(null);
      }
      await refreshPending();
    },
    [setSelectedTimesheetId, refreshPending],
  );

  return (
    <div className="rp-ts-approver-view">
      <div className="rp-ts-approver-view__header">
        <Heading type={Heading.types.H2}>
          {isPending ? 'Pending Approvals' : 'Approval History'}
        </Heading>

        {/* Inline filters (user + date for both views; status for history only) */}
        <Flex gap={Flex.gaps.XS} align={Flex.align.CENTER} className="rp-ts-approver-view__filters">
          {!isPending && (
            <div className="rp-ts-approver-view__filter-dropdown">
              <Dropdown
                size={Dropdown.sizes.SMALL}
                value={STATUS_OPTIONS.find((o) => o.value === statusFilter)}
                options={STATUS_OPTIONS}
                onChange={(option: { value: string } | null) =>
                  setStatusFilter(option?.value ?? '')
                }
                clearable={false}
                placeholder="Status"
              />
            </div>
          )}
          <Dialog
            zIndex={1000}
            content={
              <DialogContentContainer
                className="rp-resource-selection-dropdown__content"
                size={DialogContentContainer.sizes.MEDIUM}
              >
                <div className="rp-resource-selection-dropdown__search">
                  <Search
                    size="small"
                    value={userSearchValue}
                    onChange={(value: string) => setUserSearchValue(value)}
                    placeholder="Search users"
                  />
                </div>
                <Divider />
                <div className="rp-resource-selection-dropdown__list">
                  {filteredUserOptions.map((user) => (
                    <label
                      key={user.id}
                      className="rp-resource-selection-dropdown__option"
                    >
                      <Checkbox
                        checked={selectedUserIds.includes(user.id)}
                        onChange={() => toggleUser(user.id)}
                      />
                      {user.avatarUrl ? (
                        <Avatar
                          type="img"
                          src={user.avatarUrl}
                          size="small"
                          ariaLabel={user.name}
                        />
                      ) : (
                        <Avatar
                          type="text"
                          text={user.name}
                          size="small"
                          ariaLabel={user.name}
                        />
                      )}
                      <Text type={Text.types.TEXT2}>{user.name}</Text>
                    </label>
                  ))}
                </div>
              </DialogContentContainer>
            }
            hideTrigger={['clickoutside', 'esckey']}
            showTrigger={['click']}
            position="bottom-start"
          >
            <Button
              size={Button.sizes.SMALL}
              kind={Button.kinds.TERTIARY}
              className="rp-resource-selection-dropdown__trigger"
            >
              <Icon
                icon={Person}
                iconSize={16}
                className="rp-resource-selection-dropdown__trigger-icon"
              />
              <span>{userTriggerLabel}</span>
              <Icon
                icon={DropdownChevronDown}
                iconSize={16}
                className="rp-resource-selection-dropdown__chevron"
              />
            </Button>
          </Dialog>
          <input
            type="date"
            className="rp-ts-approver-view__date-input"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            title="From date"
          />
          <input
            type="date"
            className="rp-ts-approver-view__date-input"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            title="To date"
          />
        </Flex>

        <IconButton
          icon={Update}
          kind={IconButton.kinds.TERTIARY}
          size={IconButton.sizes.SMALL}
          onClick={refreshPending}
          disabled={refreshing}
          ariaLabel="Refresh"
        />
      </div>

      <div className="rp-ts-approver-view__body">
        {/* List section */}
        <div className="rp-ts-approver-view__list">
          {isPending ? (
            <TimesheetPendingList userNameMap={userNameMap} />
          ) : (
            <TimesheetHistoryList
              currentUserId={currentUserId}
              userNameMap={userNameMap}
              statusFilter={statusFilter}
              userFilterParam={userFilterParam}
              fromDate={fromDate}
              toDate={toDate}
            />
          )}
        </div>

        {/* Detail panel */}
        <div className="rp-ts-approver-view__detail">
          <TimesheetDetailPanel
            currentUserId={currentUserId}
            isAdmin={isAdmin}
            userNameMap={userNameMap}
            onActionComplete={handleActionComplete}
          />
        </div>
      </div>
    </div>
  );
};

export default TimesheetApproverView;
